import type { Patch } from "./types";
import { Voice } from "./Voice";
import { midiToFreq } from "./Synth";

export interface RenderOptions {
  /** Note to play, default middle C. */
  midi?: number;
  /** How long the note is held before release, in seconds. */
  holdTime?: number;
  sampleRate?: number;
}

// Render a patch playing a single note into a downloadable WAV, faster than
// realtime via OfflineAudioContext. Reuses the exact same Voice graph as live
// playback so the export matches what you hear.
export async function renderPatchToWav(patch: Patch, opts: RenderOptions = {}): Promise<Blob> {
  const sampleRate = opts.sampleRate ?? 44100;
  const midi = opts.midi ?? 60;
  const holdTime = opts.holdTime ?? 1.0;
  const duration = holdTime + patch.ampEnv.release + 0.15;

  const offline = new OfflineAudioContext(2, Math.ceil(sampleRate * duration), sampleRate);
  const master = new GainNode(offline, { gain: patch.masterGain });
  master.connect(offline.destination);

  const voice = new Voice(offline, patch, master);
  voice.start(0, midiToFreq(midi), patch.ampEnv);
  voice.stop(holdTime);

  const rendered = await offline.startRendering();
  return encodeWav(rendered);
}

export function encodeWav(buffer: AudioBuffer): Blob {
  const numChannels = buffer.numberOfChannels;
  const numFrames = buffer.length;
  const sampleRate = buffer.sampleRate;
  const bytesPerSample = 2;
  const blockAlign = numChannels * bytesPerSample;
  const dataSize = numFrames * blockAlign;

  const arrayBuffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(arrayBuffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeStr(8, "WAVE");
  writeStr(12, "fmt ");
  view.setUint32(16, 16, true); // PCM chunk size
  view.setUint16(20, 1, true); // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeStr(36, "data");
  view.setUint32(40, dataSize, true);

  const channels: Float32Array[] = [];
  for (let c = 0; c < numChannels; c++) channels.push(buffer.getChannelData(c));

  let offset = 44;
  for (let i = 0; i < numFrames; i++) {
    for (let c = 0; c < numChannels; c++) {
      const sample = Math.max(-1, Math.min(1, channels[c][i]));
      view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
      offset += 2;
    }
  }

  return new Blob([arrayBuffer], { type: "audio/wav" });
}
